// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import * as Sentry from '@sentry/react';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import {
  ConfirmBillingPlanInput,
  confirmBillingPlan,
} from '@/billing/api/confirm-billing-plan';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

interface UseConfirmBillingPlanOptions {
  onSuccess?: (chargesScheduled: number) => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

// Domain validation codes the BE confirm endpoint emits. Surfacing
// these inline (via the dialog's submitError alert) is the expected
// flow — they're not Sentry-worthy because they reflect user-driven
// state the FE expects to handle.
const HANDLED_API_STATUS_CODES = new Set<number>([
  3713, // Not periodic
  3714, // Not pending
  3715, // Has locked charges
  3716, // Missing schedule config
  3717, // Cascade count mismatch / label override mismatch
  3718, // Invalid service period / activation date
  ERROR_CODE_MAPPING.billing_plan_activation_date_out_of_range,
]);

// Confirms a Pending plan via POST /v1/billing-plan/.../confirm.
// Invalidates the listing, hub summary, charges dashboard, and any
// project-detail page that might surface this plan.
export function useConfirmBillingPlan(options?: UseConfirmBillingPlanOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ConfirmBillingPlanInput) => confirmBillingPlan(input),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['billing', 'plans'] }),
        queryClient.invalidateQueries({ queryKey: ['billing', 'summary'] }),
        queryClient.invalidateQueries({ queryKey: ['charges'] }),
        queryClient.invalidateQueries({ queryKey: ['project'] }),
      ]);
      options?.onSuccess?.(result.chargesScheduled);
    },
    onError: (error: AxiosError) => {
      const apiError = handleAPIError(error);
      const isHandled = HANDLED_API_STATUS_CODES.has(apiError.apiStatusCode);
      if (!isHandled && !apiError.sentToSentry) {
        Sentry.captureException(error);
      }
      options?.onError?.(
        getUserFacingErrorMessage(apiError),
        apiError.apiStatusCode,
        apiError.httpStatusCode
      );
    },
  });
}
